import { useEffect, useRef } from 'react'

import { useMercureSse } from '@/hooks/useMercureSse'
import { emitContextChanged } from '@/utils/contextChangedEvent'
import { markContextSwitchIntent } from '@/utils/contextSwitchIntent'

export interface ContextSwitchMercurePayload {
  type?: string
  contextIri?: string | null
  contextName?: string | null
  sentAt?: string
}

interface UseMercureContextUpdatesOptions {
  mercureToken: string | null
  topic: string | null
  enabled?: boolean
}

export function useMercureContextUpdates({
  mercureToken,
  topic,
  enabled = true,
}: UseMercureContextUpdatesOptions): void {
  const lastAppliedEventAtRef = useRef<string | null>(null)
  const isConnectedRef = useRef(false)
  const lastKnownContextRef = useRef<{
    contextIri: string | null
    contextName: string | null
  }>({
    contextIri:
      typeof window !== 'undefined' ? localStorage.getItem('contextIri') : null,
    contextName:
      typeof window !== 'undefined'
        ? localStorage.getItem('contextName')
        : null,
  })

  useMercureSse({
    topics: topic ? [topic] : [],
    mercureToken,
    enabled,
    onConnectionStateChange: (connected) => {
      isConnectedRef.current = connected
    },
    onData: (data) => {
      try {
        const payload = JSON.parse(data) as ContextSwitchMercurePayload
        if (payload.type !== 'context_switched') {
          return
        }
        if (
          payload.sentAt &&
          lastAppliedEventAtRef.current === payload.sentAt
        ) {
          return
        }
        lastAppliedEventAtRef.current = payload.sentAt ?? null

        if (payload.contextIri) {
          localStorage.setItem('contextIri', payload.contextIri)
        } else {
          localStorage.removeItem('contextIri')
        }
        if (payload.contextName) {
          localStorage.setItem('contextName', payload.contextName)
        } else {
          localStorage.removeItem('contextName')
        }
        markContextSwitchIntent()
        lastKnownContextRef.current = {
          contextIri: payload.contextIri || null,
          contextName: payload.contextName || null,
        }
        emitContextChanged('Mercure.context_switched')
      } catch {
        // ignore invalid payload
      }
    },
  })

  // Fallback when Mercure is unavailable:
  // on focus / visibility gain, sync local context state from storage.
  useEffect(() => {
    if (!enabled) {
      return
    }

    const syncFromStorageIfNeeded = () => {
      if (isConnectedRef.current) {
        return
      }

      const storedIri = localStorage.getItem('contextIri')
      const storedName = localStorage.getItem('contextName')
      const hasChanged =
        storedIri !== lastKnownContextRef.current.contextIri ||
        storedName !== lastKnownContextRef.current.contextName

      if (!hasChanged) {
        return
      }

      lastKnownContextRef.current = {
        contextIri: storedIri,
        contextName: storedName,
      }
      emitContextChanged('ContextFallback.focusSync')
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncFromStorageIfNeeded()
      }
    }

    window.addEventListener('focus', syncFromStorageIfNeeded)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.removeEventListener('focus', syncFromStorageIfNeeded)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [enabled])
}
